import { atom } from 'jotai';
import isEqual from 'lodash/isEqual';

import { QuoteDetailRecord, QuoteFormState } from '../types/quote';

const initialState = (): QuoteFormState => ({
  type: null,
  accountId: undefined,
  facilityId: undefined,
  validUntil: null,
  expectedShipDate: null,
  paymentTerm: '',
  freightTerm: '',
  paymentCurrency: '',
  freightCoveredByCustomer: false,
  notes: '',
});

const mapQuoteToQuoteForm = (quote: QuoteDetailRecord): QuoteFormState => ({
  type: quote.type,
  accountId: quote.accountId,
  facilityId: quote.facilityId ?? undefined,
  validUntil: quote.validUntil ? quote.validUntil.toString() : null,
  expectedShipDate: quote.expectedShipDate
    ? quote.expectedShipDate.toString()
    : null,
  paymentTerm: quote.paymentTerm ?? '',
  freightTerm: quote.freightTerm ?? '',
  paymentCurrency: quote.paymentCurrency ?? '',
  freightCoveredByCustomer: quote.freightCoveredByCustomer ?? false,
  notes: quote.notes ?? '',
});

// 🔘🔘🔘 PLAIN ATOMS
export const $quoteForm = atom<QuoteFormState>(initialState());
export const $discardBaseline = atom<QuoteFormState | null>(null);
export const $lastPopulatedQuoteId = atom<string | undefined>(undefined);
export const $isQuoteFormPopulated = atom<boolean>(false);

// 🟢🟢🟢 COMPUTED DATA -> has unsaved changes
export const $hasUnsavedChanges = atom((get) => {
  const form = get($quoteForm);
  const baseline = get($discardBaseline);

  if (!baseline) return false;

  return !isEqual(form, baseline);
});

// 🟠🟠🟠 ACTION -> update form fields
export const $updateQuoteForm = atom(
  null,
  (get, set, update: Partial<QuoteFormState>) => {
    set($quoteForm, { ...get($quoteForm), ...update });
  },
);

// 🟠🟠🟠 ACTION -> populate form from quote
export const $populateQuoteFormFromQuote = atom(
  null,
  (get, set, quote: QuoteDetailRecord) => {
    const formState = mapQuoteToQuoteForm(quote);
    set($quoteForm, formState);
    set($discardBaseline, formState);
    set($lastPopulatedQuoteId, quote.id);
    set($isQuoteFormPopulated, true);
  },
);

// 🟠🟠🟠 ACTION -> reset form to baseline
export const $resetQuoteFormToQuote = atom(null, (get, set) => {
  const baseline = get($discardBaseline);
  if (baseline) {
    set($quoteForm, baseline);
  }
});

// 🟠🟠🟠 ACTION -> populate form (with quote change detection)
export const $populateQuoteFormAction = atom(
  null,
  (get, set, quote: QuoteDetailRecord | null | undefined) => {
    if (!quote) return;

    const currentQuoteId = quote.id;
    const lastQuoteId = get($lastPopulatedQuoteId);

    if (currentQuoteId !== lastQuoteId) {
      set($isQuoteFormPopulated, false);
    }

    if (!get($isQuoteFormPopulated)) {
      set($populateQuoteFormFromQuote, quote);
    }
  },
);

// 🔘🔘🔘 PLAIN ATOMS -> Create flow
export const $isQuoteFormSubmitting = atom<boolean>(false);

// 🟢🟢🟢 COMPUTED DATA -> can submit quote form
export const $canSubmitQuoteForm = atom((get) => {
  const form = get($quoteForm);
  const isSubmitting = get($isQuoteFormSubmitting);
  return Boolean(form.accountId) && !isSubmitting;
});

// 🟠🟠🟠 ACTION -> reset form for create mode
export const $resetQuoteFormForCreate = atom(null, (_get, set) => {
  set($quoteForm, { ...initialState(), type: 'Sales' });
  set($discardBaseline, null);
  set($lastPopulatedQuoteId, undefined);
  set($isQuoteFormPopulated, false);
  set($isQuoteFormSubmitting, false);
});
